import { ChangeEvent, FC, useCallback, useRef, useState } from 'react';
import { SingleValue } from 'react-select';
import styled from 'styled-components';
import { ModalWrapper } from 'modals/styled';
import { FlexRow } from 'shared/Typhography/flex-box';
import { FlexColumn, Text } from 'shared/Typhography';
import { theme } from 'shared/styling';
import { useLiveData } from 'hooks/useLiveData';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { storage } from 'services';

import { Button } from '../Button';
import { Splash } from '../Splash';
import { Option, Select } from '../Select';

const StyledModalWrapper = styled(ModalWrapper)`
  padding: 15px 20px;
  min-width: 30vw;

  gap: 20px;
`;

const Title = styled(Text)`
  flex: 1;
  font-size: 24px;
  font-weight: 500;
`;

const XIcon = styled.span`
  cursor: pointer;
  font-size: 25px;
`;

const MessageTextArea = styled.textarea`
  min-width: 300px;
  width: 90%;
  max-width: 90%;
  min-height: 256px;
  max-height: 500px;

  font-size: 20px;

  padding: 10px;
  border-radius: 4px;
  border: 1px solid hsl(0, 0%, 80%);
`;

const REJECTION_MODAL_TITLES = {
  puzzle: 'Puzzle Reject Message',
  artist: 'Artist Reject Message',
};

export interface RejectionModalProps {
  rejectionModalType: 'artist' | 'puzzle';
  onClose: () => void;
  onSubmit: (message: string) => Promise<void> | void;
  onCancel?: () => void;
  id?: string;
}

const PREDEFINED_PUZZLE_MESSAGES_QUERY = {
  collectionPath: 'rejectionReasons/puzzles/predefinedTextMessages',
};

const PREDEFINED_ARTIST_MESSAGES_QUERY = {
  collectionPath: 'rejectionReasons/artists/predefinedTextMessages',
};

interface IRejectionMessage {
  title: string;
  message: string;
}

export const RejectionModal: FC<RejectionModalProps> = ({
  rejectionModalType,
  onClose,
  onSubmit: onConfirm,
  onCancel,
  id,
}) => {
  const mediaUploadRef = useRef<HTMLInputElement>(null);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const [isUploading, setUploading] = useState<boolean>(false);

  const { data: predefinedTextMessages } = useLiveData<IRejectionMessage>(
    rejectionModalType === 'puzzle'
      ? PREDEFINED_PUZZLE_MESSAGES_QUERY
      : PREDEFINED_ARTIST_MESSAGES_QUERY,
  );

  const onSubmit = async () => {
    try {
      setIsLoading(true);

      await onConfirm?.(message);

      setIsLoading(false);
      onClose();
    } catch (err) {
      console.log(err);
    }
  };

  const onSelect = useCallback((e: SingleValue<Option>) => {
    const newVal = e?.value?.toString() || '';
    setMessage((prevVal) => (prevVal ? `${prevVal}\n\n${newVal}` : newVal));
  }, []);

  const uploadMedia = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event?.target?.files?.[0];
    if (!file) return;
    setUploading(true);

    const imageReference = ref(storage, `emailMedia/${id}/${file.name}`);
    await uploadBytes(imageReference, file, { contentType: file.type });

    const publicUrl = await getDownloadURL(imageReference);
    const addValue = `<img style="width: 100%; height: auto; display: block" src="${publicUrl}"/>`;
    setMessage((previusValue) => (previusValue ? `${previusValue}\n\n${addValue}` : addValue));

    setUploading(false);
  };

  return (
    <StyledModalWrapper>
      <FlexRow justifyContent="flex-end">
        <Title>{REJECTION_MODAL_TITLES[rejectionModalType]}</Title>
        <XIcon
          onClick={() => {
            if (!isLoading) onClose();
          }}
        >
          &#10006;
        </XIcon>
      </FlexRow>
      <FlexColumn gap="10px">
        <Select
          id="select-artist-rejection-reason"
          label=""
          placeholder="Select rejection reason"
          onChange={onSelect}
          options={predefinedTextMessages.map((x) => ({
            value: `${x.title}\n\n${x.message}`,
            label: x.title,
          }))}
          value={null}
          style={{ width: '100%' }}
          isSearchable={false}
        />
        {rejectionModalType === 'puzzle' && (
          <>
            <Button
              style={{ padding: '8px', width: '100%' }}
              disabled={isUploading}
              onClick={() => {
                if (!mediaUploadRef?.current || isUploading) return;
                mediaUploadRef?.current.click();
              }}
            >
              Upload Media
            </Button>
            <input
              ref={mediaUploadRef}
              multiple={false}
              style={{ display: 'none' }}
              type="file"
              onChange={uploadMedia}
            />
          </>
        )}
        <div style={{ flex: 1, width: '100%' }}>
          <MessageTextArea onChange={(event) => setMessage(event?.target?.value)} value={message} />
        </div>
      </FlexColumn>

      <FlexRow dimensions={{ width: '100%' }} style={{ paddingBottom: 10 }} alignItems="center">
        <div style={{ flex: 1 }}>
          <Button
            variant="cancel"
            height={40}
            style={{ padding: '10px 15px', minWidth: '10vw' }}
            disabled={isLoading}
            onClick={onCancel ?? onClose}
          >
            {/* TODO Add Translations */}
            Cancel
          </Button>
        </div>
        <div style={{ flex: 1 }}>
          <Button
            height={40}
            style={{ padding: '10px 15px', minWidth: '10vw' }}
            disabled={isLoading || !message || message?.length == 0 || isUploading}
            onClick={onSubmit}
          >
            {/* TODO Add Color to Loading component */}
            {/* TODO Take color from theme */}
            {(isLoading || isUploading) && (
              <Splash width={20} height={20} color={theme.colors.primaryInverted} />
            )}
            {!(isLoading || isUploading) && (
              <>
                {/* TODO Add translations */}
                Confirm
              </>
            )}
          </Button>
        </div>
      </FlexRow>
    </StyledModalWrapper>
  );
};
